import React, { useEffect, useRef, useCallback } from "react";
import { FiArrowLeft, FiX } from "react-icons/fi";

/**
 * Right-side overlay drawer. Slides in via transform (compositor-friendly),
 * locks body scroll, and closes on Escape, outside-click, or the close button.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the drawer is open.
 * @param {() => void} props.onClose - Close handler.
 * @param {() => void} [props.onBack] - Renders a Back arrow before the title,
 *   for a drawer that replaced another one and can hand the screen back to it.
 *   Distinct from `onClose` on purpose: Back returns to where you came from,
 *   the ✕ (and Escape, and an outside click) dismisses the stack entirely.
 * @param {React.ReactNode} [props.title] - Header title.
 * @param {React.ReactNode} props.children - Drawer body content.
 * @param {number|string} [props.width=360] - Panel width from the `sm` breakpoint
 *   up; below it the panel is always full-width. A number is treated as pixels;
 *   a string is used verbatim, so a caller can say `min(900px, 92vw)`.
 * @param {string} [props.bodyClassName] - Overrides the body's own scroll and
 *   padding. Pass `"flex-1 min-h-0 overflow-hidden"` for content that pins its
 *   own header and scrolls internally, which the default would double up on.
 */
export const Drawer = ({
  isOpen,
  onClose,
  onBack,
  title,
  children,
  width = 360,
  bodyClassName = "flex-1 overflow-y-auto p-4",
}) => {
  const panelRef = useRef(null);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        handleClose();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "auto";
    };
  }, [handleClose, isOpen]);

  return (
    <div
      className={`fixed inset-0 z-50 ${isOpen ? "" : "pointer-events-none"}`}
      aria-hidden={!isOpen}
    >
      <div
        className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0"
        }`}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : undefined}
        style={{ "--drawer-width": typeof width === "number" ? `${width}px` : width }}
        className={`absolute top-0 right-0 flex h-full w-full max-w-full flex-col bg-white shadow-xl transition-transform duration-300 sm:w-[var(--drawer-width)] ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-shrink-0 items-center justify-between gap-2 border-b border-gray-200 px-4 py-3">
          <div className="flex min-w-0 items-center gap-1">
            {onBack ? (
              <button
                type="button"
                onClick={onBack}
                aria-label="Back"
                title="Back"
                // Pulled into the padding so the title keeps its own left
                // edge whether or not this button is there.
                className="-ml-2 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary"
              >
                <FiArrowLeft size={20} />
              </button>
            ) : null}
            <h2 className="truncate text-lg font-bold text-gray-800">{title}</h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className="-mr-2 flex h-10 w-10 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
          >
            <FiX size={20} />
          </button>
        </div>
        <div className={bodyClassName}>{children}</div>
      </div>
    </div>
  );
};
